// 拦截器出入参加解密

import { encode, decode } from "./obfuscator";
import qs from "qs";

// 加密方式
const cryptoMethods = {
  // Base64
  Base64: { encode, decode },
};

/**
 * 对字符串进行加密处理
 * @param {any} str - 待加密的内容
 * @param {any} cryptoMethod - 加密方式（默认 Base64）
 * @returns {any} 加密后的字符串
 */
export const encodeString = (str, cryptoMethod = "Base64") => {
  // null/undefined 直接返回
  if (str === null || str === undefined || str === "") {
    return str;
  }

  try {
    return cryptoMethods[cryptoMethod].encode(str);
  } catch (error) {
    return str;
  }
};

/**
 * 对字符串解密
 * @param {any} str - 待解密的内容
 * @param {any} cryptoMethod - 解密方式（默认 Base64）
 * @returns {any} 解密后的字符串
 */
export const decodeString = (str, cryptoMethod = "Base64") => {
  // null/undefined 直接返回
  if (str === null || str === undefined || str === "") {
    return str;
  }

  try {
    return cryptoMethods[cryptoMethod].decode(str);
  } catch (error) {
    return str;
  }
}

/**
 * 递归加密/解密对象/数组
 * @param {any} data - 待处理的数据（对象/数组）
 * @param {string[]} keys - 需要加密/解密的字段名
 * @param {boolean} isEncode - true: 加密，false: 解密
 * @param {string} cryptoMethod - 加密方法，默认使用 Base64
 * @returns {any} 处理后的数据（异常时返回原始数据）
 */
 const cryptoObject = ({
  data,
  keys = [],
  isEncode,
  cryptoMethod = "Base64"
}) => {
  // null/undefined/非对象数组/无加解密字段名直接返回
  if (
    data == null ||
    (typeof data !== "object" && !Array.isArray(data)) ||
    !keys ||
    keys.length === 0
  ) {
    return data;
  }

  try {
    // 处理数组：递归处理每个元素
    if (Array.isArray(data)) {
      return data.map(item => cryptoObject({ data: item, keys, isEncode, cryptoMethod }))
    }

    const result = {};
    const targetKeys = new Set(keys);

    Object.entries(data).forEach(([key, value]) => {
      // 嵌套对象/数组递归处理
      if (value !== null && typeof value === "object") {
        result[key] = cryptoObject({ data: value, keys, isEncode, cryptoMethod })
      }
      // 加密/解密
      else if (targetKeys.has(key)) {
        result[key] = isEncode
          ? encodeString(value, cryptoMethod)
          : decodeString(value, cryptoMethod);
      }
      // 原值
      else {
        result[key] = value;
      }
    });

    return result;
  } catch (error) {
    console.warn("cryptoObject 处理数据异常：", error);
    return data; // 异常时返回原始数据
  }
};

// 数据分字段加密
export const encodeFields = (config = {}) => cryptoObject({ isEncode: true, cryptoMethod: "Base64", ...config });

// 数据分字段解密
export const decodeFields = (config = {}) => cryptoObject({ isEncode: false, cryptoMethod: "Base64", ...config });

// 数据整体加密
export const encodeObject = (config = {}) => {
  const { data, cryptoMethod } = config
  return { encodeParam: encodeString(data, cryptoMethod) }
}

// 数据整体解密
export const decodeObject = (config = {}) => {
  const { data, cryptoMethod } = config
  return decodeString(data, cryptoMethod)
}

// 数据整体序列化再加密
export const encodeStringify = (config = {}) => {
  const { data, cryptoMethod } = config
  const encodeParam = encodeString(qs.stringify(data), cryptoMethod)
  return { encodeParam }
}

/**
 * 递归加密/解密对象/数组
 * @param {any} data - 待处理的数据（对象/数组）
 * @param {string[]} keys - 需要加密/解密的路径(['a.b', 'c.d'])
 * @param {boolean} isEncode - true: 加密，false: 解密
 * @param {string} cryptoMethod - 加密方法，默认使用 Base64
 * @returns {any} 处理后的数据（异常时返回原始数据）
 */
const cryptoObjectByPath = ({
  data,
  keys = [],
  isEncode,
  cryptoMethod = "Base64"
}) => {
  // null/undefined/非对象数组/无加解密字段名直接返回
  if (
    data == null ||
    (typeof data !== "object" && !Array.isArray(data)) ||
    !keys ||
    keys.length === 0
  ) {
    return data;
  }

  try {
    // 将路径转换为映射结构，便于快速查找
    const pathMap = {};
    keys.forEach(key => {
      const parts = key.split('.');
      let current = pathMap;
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        if (!current[part]) current[part] = {};
        current = current[part];
      }
      // 标记叶子节点
      current._isLeaf = true;
    });

    const processValue = (value, pathObj, currentPath = []) => {
      if (value === null || value === undefined) {
        return value;
      }

      // 如果是数组
      if (Array.isArray(value)) {
        return value.map((item, index) => {
          const newPath = [...currentPath, index.toString()];
          // 递归处理数组中的每个元素
          return processValue(item, pathObj, newPath);
        });
      }

      // 如果是对象
      if (typeof value === "object") {
        const processed = {};
        Object.entries(value).forEach(([key, val]) => {
          const newPath = [...currentPath, key];
          
          // 检查当前路径是否在keys中
          if (pathObj[key] && pathObj[key]._isLeaf) {
            processed[key] = isEncode
              ? encodeString(val, cryptoMethod)
              : decodeString(val, cryptoMethod);
          } else if (val !== null && typeof val === "object") {
            // 递归处理嵌套对象或数组
            processed[key] = processValue(val, pathObj[key] || {}, newPath);
          } else {
            // 普通值
            processed[key] = val;
          }
        });
        return processed;
      }

      // 基本类型值直接返回
      return value;
    };

    return processValue(data, pathMap);
  } catch (error) {
    console.warn("cryptoObjectByPath 处理数据异常：", error);
    return data;
  }
};

// 数据按路径加密
export const encodeFieldsByPath = (config = {}) => cryptoObjectByPath({ isEncode: true, cryptoMethod: "Base64", ...config });

// 数据按路径解密
export const decodeFieldsByPath = (config = {}) => cryptoObjectByPath({ isEncode: false, cryptoMethod: "Base64", ...config });
